import TextArea from "../../../../../Components/Common/TextArea/TextArea";
import Select from "../../../../../Components/Common/Select/Select";
import React from "react";
import Button from "../../../../../Components/Common/Button/Button";
import Input from "../../../../../Components/Common/Input/Input";
import ErrorMessage from "../../../../../Components/Common/ErrorMessage/ErrorMessage";
import { CHAR_LIMIT } from "../../../../../utils/Common/Constant";
import DateSelect from "../../../../../Components/Common/DateSelect/DateSelect";
import { NewProjectFormValues } from "../types";
import { FormikErrors, FormikTouched } from "formik";

const LIEN_TYPE_OPTIONS = [
  { value: "", label: "- Select -" },
  { value: "AGGREGATED", label: "Aggregated" },
  { value: "PER_DRAW", label: "Per Draw" },
];

interface YesNoRowProps {
  title: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  disableNo?: boolean;
}

function YesNoRow({ title, value, onChange, hint, disableNo }: YesNoRowProps) {
  return (
    <div className="ynRow">
      <div className="ynQuestion">
        {title}
        {hint ? <span className="hint">{hint}</span> : null}
      </div>
      <div className="ynBtns">
        <Button
          className={`ynBtn ${value === "yes" ? "selectedYes" : ""}`}
          onClick={() => onChange("yes")}
        >
          Yes
        </Button>
        <Button
          className={`ynBtn ${value === "no" ? "selectedNo" : ""}`}
          onClick={() => onChange("no")}
          disabled={disableNo}
        >
          No
        </Button>
      </div>
    </div>
  );
}

type FlatErrors = Record<string, string | undefined>;
type FlatTouched = Record<string, boolean | undefined>;

interface Step5RequirementsProps {
  values: NewProjectFormValues;
  errors: FormikErrors<NewProjectFormValues>;
  touched: FormikTouched<NewProjectFormValues>;
  setFieldValue: (name: string, value: string) => void;
  onChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void;
  onNext: () => void;
  onBack: () => void;
}

export default function Step5Requirements({
  values,
  errors,
  touched,
  setFieldValue,
  onChange,
  onNext,
  onBack,
}: Step5RequirementsProps) {
  const req = values.requirements;
  const flatErrors = errors as unknown as FlatErrors;
  const flatTouched = touched as unknown as FlatTouched;

  return (
    <div className="stepPanel active">
      <div className="card">
        <div className="cardTitle">Project Requirements</div>

        <YesNoRow
          title="Will CSC be required to attend a monthly meeting with the inspection?"
          value={req.mtg}
          onChange={(v) => setFieldValue("requirements.mtg", v)}
        />
        <YesNoRow
          title="Will CSC be tracking lien waivers?"
          value={req.lien}
          onChange={(v) => setFieldValue("requirements.lien", v)}
        />
        <YesNoRow
          title="Is there a lien waiver limit?"
          hint="If so, is this aggregate or per draw?"
          value={req.lienLimit}
          onChange={(v) => setFieldValue("requirements.lienLimit", v)}
        />

        {req.lienLimit === "yes" && (
          <div className="ynConditional visible">
            <div className="fieldGroup cols2">
              <div className="field">
                <label>
                  Limit Amount <span className="req">*</span>
                </label>
                <div className="inputWithPrefix">
                  <span className="prefixSymbol">$</span>
                  <Input
                    type="text"
                    id="lienLimitAmount"
                    name="requirements.lienLimitAmount"
                    value={req.lienLimitAmount}
                    onChange={onChange}
                    placeholder="amount"
                  />
                </div>
                <ErrorMessage
                  value={req.lienLimitAmount}
                  touched={flatTouched["requirements.lienLimitAmount"]}
                  error={flatErrors["requirements.lienLimitAmount"]}
                />
              </div>
              <div className="field">
                <label>
                  Type <span className="req">*</span>
                </label>
                <Select
                  name="requirements.lienLimitType"
                  options={LIEN_TYPE_OPTIONS}
                  value={req.lienLimitType}
                  onChange={onChange}
                  height={"38px"}
                />
                <ErrorMessage
                  value={req.lienLimitType}
                  touched={flatTouched["requirements.lienLimitType"]}
                  error={flatErrors["requirements.lienLimitType"]}
                />
              </div>
            </div>
          </div>
        )}

        <YesNoRow
          title="Are lien waivers required to be notarized?"
          value={req.notar}
          onChange={(v) => setFieldValue("requirements.notar", v)}
        />
        <YesNoRow
          title="Will sub-contractor / supplier invoices be required?"
          value={req.subInv}
          onChange={(v) => setFieldValue("requirements.subInv", v)}
        />
        <YesNoRow
          title="Will a Pre-Construction Review be required?"
          hint="If yes, the assigned general contractor will be notified to complete the pre-construction checklist."
          value={req.precon}
          onChange={(v) => setFieldValue("requirements.precon", v)}
        />

        {req.precon === "yes" && (
          <div className="ynConditional visible">
            <div className="field">
              <DateSelect
                id="preconDate"
                label="Approximate date to receive Pre-Construction Report"
                value={values.preconDate}
                onChange={(val) => setFieldValue("preconDate", val)}
              />
              {touched.preconDate && errors.preconDate && (
                <span className="field-error">{errors.preconDate}</span>
              )}
            </div>
          </div>
        )}

        <YesNoRow
          title="Is there a borrower's first funds equity contribution requirement?"
          value={req.equity}
          onChange={(v) => setFieldValue("requirements.equity", v)}
        />
        {req.equity === "yes" && (
          <div className="ynConditional visible">
            <div className="field">
              <label>
                Amount <span className="req">*</span>
              </label>
              <div className="inputWithPrefix">
                <span className="prefixSymbol">$</span>
                <Input
                  type="text"
                  id="equityAmount"
                  name="requirements.equityAmount"
                  value={req.equityAmount}
                  onChange={onChange}
                  placeholder="amount"
                />
              </div>
              <ErrorMessage
                value={req.equityAmount}
                touched={flatTouched["requirements.equityAmount"]}
                error={flatErrors["requirements.equityAmount"]}
              />
            </div>
            <div className="field mt-3">
              <label>Please describe the equity contribution requirement</label>
              <TextArea
                name="requirements.equityDesc"
                value={req.equityDesc}
                onChange={onChange}
                maxChars={CHAR_LIMIT.DESCRIPTION}
              />
              {flatTouched["requirements.equityDesc"] &&
                flatErrors["requirements.equityDesc"] && (
                  <span className="field-error">
                    {flatErrors["requirements.equityDesc"]}
                  </span>
                )}
            </div>
          </div>
        )}

        <YesNoRow
          title="Will CSC be tracking soft costs for this project?"
          value={req.soft}
          onChange={(v) => setFieldValue("requirements.soft", v)}
        />

        <YesNoRow
          title="Will owner approval be required for change orders?"
          value={req.changeOrder}
          onChange={(v) => {
            setFieldValue("requirements.changeOrder", v);
            setFieldValue(
              "requirements.changeOrderThreshold",
              v === "yes" ? "0" : "",
            );
          }}
        />

        <YesNoRow
          title="Will retainage be withheld on this project?"
          hint="Retainage is a portion of each draw payment held back until project completion."
          value={req.retainage}
          onChange={(v) => {
            setFieldValue("requirements.retainage", v);
            if (v === "no")
              setFieldValue("requirements.retainagePercentage", "");
          }}
        />

        {req.retainage === "yes" && (
          <div className="ynConditional visible">
            <div className="field">
              <label>
                Retainage percentage <span className="req">*</span>
              </label>
              <div className="inputWithPrefix">
                <span className="prefixSymbol">%</span>
                <Input
                  type="text"
                  id="retainagePercentage"
                  name="requirements.retainagePercentage"
                  value={req.retainagePercentage}
                  onChange={onChange}
                  placeholder="e.g. 10"
                />
              </div>
              <ErrorMessage
                value={req.retainagePercentage}
                touched={flatTouched["requirements.retainagePercentage"]}
                error={flatErrors["requirements.retainagePercentage"]}
              />
            </div>
          </div>
        )}

        <YesNoRow
          title="Will there be a lender contingency budget for this project?"
          hint="The contingency is a pre-approved construction budget reserve. The GC draws from it directly in draw requests for unforeseen cost overages — no lender approval required."
          value={req.contingency}
          onChange={(v) => {
            setFieldValue("requirements.contingency", v);
            if (v === "no") setFieldValue("requirements.contingencyAmount", "");
          }}
        />

        {req.contingency === "yes" && (
          <div className="ynConditional visible">
            <div className="field">
              <label>
                Contingency Amount <span className="req">*</span>
              </label>
              <div className="inputWithPrefix">
                <span className="prefixSymbol">$</span>
                <Input
                  type="text"
                  id="contingencyAmount"
                  name="requirements.contingencyAmount"
                  value={req.contingencyAmount}
                  onChange={onChange}
                  placeholder="amount"
                />
              </div>
              <ErrorMessage
                value={req.contingencyAmount}
                touched={flatTouched["requirements.contingencyAmount"]}
                error={flatErrors["requirements.contingencyAmount"]}
              />
            </div>
          </div>
        )}
        <YesNoRow
          title="Pre-construction report fees"
          hint="Fee charged to the borrower for preparing the pre-construction review report before the project begins."
          value="yes"
          onChange={() => {}}
          disableNo
        />
        <div className="ynConditional visible">
          <div className="field">
            <label>
              Fee Amount <span className="req">*</span>
            </label>
            <div className="inputWithPrefix">
              <span className="prefixSymbol">$</span>
              <Input
                type="text"
                id="preConstructionReportFeeAmount"
                name="requirements.preConstructionReportFeeAmount"
                value={req.preConstructionReportFeeAmount}
                onChange={onChange}
                placeholder="amount"
              />
            </div>
            <ErrorMessage
              value={req.preConstructionReportFeeAmount}
              touched={
                flatTouched["requirements.preConstructionReportFeeAmount"]
              }
              error={flatErrors["requirements.preConstructionReportFeeAmount"]}
            />
          </div>
        </div>
        <YesNoRow
          title="Construction report authoring fees (Per report)"
          hint="Fee charged to the borrower for each inspection/draw report authored during construction."
          value="yes"
          onChange={() => {}}
          disableNo
        />
        <div className="ynConditional visible">
          <div className="field">
            <label>
              Fee Amount <span className="req">*</span>
            </label>
            <div className="inputWithPrefix">
              <span className="prefixSymbol">$</span>
              <Input
                type="text"
                id="constructionReportAuthoringFeeAmount"
                name="requirements.constructionReportAuthoringFeeAmount"
                value={req.constructionReportAuthoringFeeAmount}
                onChange={onChange}
                placeholder="amount"
              />
            </div>
            <ErrorMessage
              value={req.constructionReportAuthoringFeeAmount}
              touched={
                flatTouched["requirements.constructionReportAuthoringFeeAmount"]
              }
              error={
                flatErrors["requirements.constructionReportAuthoringFeeAmount"]
              }
            />
          </div>
        </div>

        <div className="field mt-3">
          <label>Any other special requirements?</label>
          <TextArea
            id="specialReqs"
            name="specialReqs"
            value={values.specialReqs}
            onChange={onChange}
            className="specialReqsTextarea"
            maxChars={CHAR_LIMIT.DESCRIPTION}
          />
          <ErrorMessage
            value={values.specialReqs}
            touched={touched.specialReqs}
            error={errors.specialReqs}
          />
        </div>
      </div>
      <div className="btnRow">
        <Button className="btn btnSecondary" onClick={onBack}>
          ← Back
        </Button>
        <Button className="btn btnPrimary" onClick={onNext}>
          Next: Review →
        </Button>
      </div>
    </div>
  );
}
